import { useCallback, useMemo } from "react";
import { useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useQueryScope } from "@/hooks/use-query-scope";
import { useScopedQuery } from "@/hooks/use-scoped-query";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields, listChanges } from "@/lib/changed-fields";
import type { QueryScope } from "@/queries/scope";
import {
  cachedSkillsPageRow,
  invalidateSkills,
  memberSkillsKey,
  memberSkillsQuery,
  mySkillsKey,
  mySkillsQuery,
  patchSkillBank,
  patchSkillsPages,
  skillBankKey,
  skillBankQuery,
  skillUsersKey,
  skillUsersQuery,
} from "@/queries/skills";
import { useStore } from "@/stores/store-context";
import type {
  TSkill,
  TSkillBulkImportResult,
  TSkillBulkImportRow,
  TUserSkillAssignment,
} from "@/types/skill";

/** One empty list each, so a reader's memos see the same value until data loads. */
const NO_SKILLS: TSkill[] = [];
const NO_ASSIGNMENTS: TUserSkillAssignment[] = [];
const NO_IDS: string[] = [];

interface SkillReadOptions {
  /** `false` reads nothing, e.g. while the picker that shows it is closed. */
  readonly enabled?: boolean;
}

/** A skill read and whether it has answered. */
export interface SkillRead<T> {
  readonly data: T;
  /** The read has answered, so an empty `data` really is empty. */
  readonly loaded: boolean;
  /** The read is waiting on its first answer; a failed read has settled. */
  readonly isLoading: boolean;
  /** The read failed, or `null`. */
  readonly error: Error | null;
  /** Read again, for a "try again" after a failure. */
  readonly retry: () => void;
}

/** The shared shape of the four skill reads. */
function answer<T>(
  query: {
    data: T | undefined;
    isSuccess: boolean;
    isPending: boolean;
    error: Error | null;
    refetch: () => Promise<unknown>;
  },
  mode: string,
  on: boolean,
  empty: T,
): SkillRead<T> {
  return {
    data: query.data ?? empty,
    loaded: query.isSuccess,
    isLoading: on && (mode === "waiting" || query.isPending),
    error: query.error,
    retry: () => {
      query.refetch().catch((error: unknown) => {
        console.warn("[skills] retrying a skill read failed", { error });
      });
    },
  };
}

/**
 * The workspace's skills, archived included (`skills.bank`, NG-2802). A reader
 * that offers only live skills drops the archived ones itself. The reader
 * observes the cached query, so a skill write in this tab, or another tab's
 * through the `skills:bank` hint, re-reads what it shows.
 *
 * Reads the signed-in user from a store, so the caller must be an `observer`.
 */
export function useSkillBank(
  workspaceSlug: string | undefined,
  { enabled = true }: SkillReadOptions = {},
): SkillRead<TSkill[]> {
  const { workspaceSkillService } = useStore();
  const { mode, query } = useScopedQuery(
    workspaceSlug,
    (scope) => skillBankQuery(workspaceSkillService, scope),
    { enabled, flag: null },
  );
  return answer(query, mode, enabled && Boolean(workspaceSlug), NO_SKILLS);
}

/** One skill's holders (`skills.users`). */
export function useSkillUsers(
  workspaceSlug: string | undefined,
  skillId: string,
  { enabled = true }: SkillReadOptions = {},
): SkillRead<TUserSkillAssignment[]> {
  const { workspaceSkillService } = useStore();
  const on = enabled && Boolean(skillId);
  const { mode, query } = useScopedQuery(
    workspaceSlug,
    (scope) => skillUsersQuery(workspaceSkillService, scope, skillId),
    { enabled: on, flag: null },
  );
  return answer(query, mode, on && Boolean(workspaceSlug), NO_ASSIGNMENTS);
}

/**
 * A one-shot read of the skill bank for a page that seeds its own state from
 * it once (Plan Work's required skills): answers from the cache when it holds
 * the bank, and reads it otherwise, leaving it cached. `null` until the
 * session has resolved, so the caller waits rather than seeding from nothing;
 * a new function once it has, so an effect keyed on it runs then.
 */
export function useEnsureSkillBank(workspaceSlug: string): (() => Promise<TSkill[]>) | null {
  const { workspaceSkillService } = useStore();
  const client = useQueryClient();
  const scope = useQueryScope(workspaceSlug);
  const tenant = scope?.tenant;
  const principal = scope?.principal;
  return useMemo(
    () =>
      tenant !== undefined && principal !== undefined
        ? () => client.ensureQueryData(skillBankQuery(workspaceSkillService, { tenant, principal }))
        : null,
    [client, tenant, principal, workspaceSkillService],
  );
}

/**
 * Read one skill's holders fresh from the server, whatever the cache holds,
 * and leave the answer in `skills.users` for the readers on it. For a picker
 * whose save replaces the whole set: it must seed its draft from the server's
 * current holders, never a cached copy another tab may have outdated.
 */
export function useFetchSkillUsers(
  workspaceSlug: string,
): (skillId: string) => Promise<TUserSkillAssignment[]> {
  const { workspaceSkillService } = useStore();
  const client = useQueryClient();
  const scope = useQueryScope(workspaceSlug);
  return useCallback(
    (skillId: string) => {
      if (!scope) return Promise.reject(new Error("[skills] read before the session resolved"));
      return client.fetchQuery({
        ...skillUsersQuery(workspaceSkillService, scope, skillId),
        staleTime: 0,
      });
    },
    [client, scope, workspaceSkillService],
  );
}

/** One member's skills (`skills.member`). */
export function useMemberSkills(
  workspaceSlug: string | undefined,
  memberId: string,
  { enabled = true }: SkillReadOptions = {},
): SkillRead<TUserSkillAssignment[]> {
  const { workspaceSkillService } = useStore();
  const on = enabled && Boolean(memberId);
  const { mode, query } = useScopedQuery(
    workspaceSlug,
    (scope) => memberSkillsQuery(workspaceSkillService, scope, memberId),
    { enabled: on, flag: null },
  );
  return answer(query, mode, on && Boolean(workspaceSlug), NO_ASSIGNMENTS);
}

/** The ids of the signed-in member's skills (`skills.mine`). */
export function useMySkillIds(
  workspaceSlug: string | undefined,
  { enabled = true }: SkillReadOptions = {},
): SkillRead<string[]> {
  const { workspaceSkillService } = useStore();
  const { mode, query } = useScopedQuery(
    workspaceSlug,
    (scope) => mySkillsQuery(workspaceSkillService, scope),
    { enabled, flag: null },
  );
  return answer(query, mode, enabled && Boolean(workspaceSlug), NO_IDS);
}

/** Server-side cap on a list row's `preview_user_ids` (`bounded_preview_map`). */
const PREVIEW_USER_ID_LIMIT = 3;

/** The first few holder ids as the list endpoint would order them.
 *
 *  The server's preview orders assignments by `(created_at, id)`, so the cached
 *  row has to sort the same way or the closed cell shows different faces before
 *  and after a refresh. Sorting is required, not defensive: the PUT re-reads
 *  without an `order_by`, so its response arrives in whatever order Postgres
 *  returns. The `id` tiebreak carries the weight in practice — a replace-save
 *  writes the whole set in one `bulk_create`, so the rows share a `created_at`
 *  — and comparing canonical lowercase UUID strings matches Postgres's `uuid`
 *  ordering, since the hyphens sit at fixed positions and hex digits sort in
 *  nibble order. */
function previewUserIds(assignments: TUserSkillAssignment[]): string[] {
  return [...assignments]
    .sort((a, b) => a.created_at.localeCompare(b.created_at) || a.id.localeCompare(b.id))
    .slice(0, PREVIEW_USER_ID_LIMIT)
    .map((assignment) => assignment.user);
}

export interface SkillWriters {
  createSkill(name: string, source?: AnalyticsSource): Promise<TSkill>;
  bulkImport(rows: TSkillBulkImportRow[]): Promise<TSkillBulkImportResult>;
  updateSkill(
    skillId: string,
    data: { name?: string; sort_order?: number },
    source?: AnalyticsSource,
  ): Promise<TSkill>;
  archiveSkill(skillId: string, source?: AnalyticsSource): Promise<TSkill>;
  unarchiveSkill(skillId: string, source?: AnalyticsSource): Promise<TSkill>;
  deleteSkill(skillId: string, source?: AnalyticsSource): Promise<void>;
  /** Replace a skill's holders; `userIds` is the whole new set. */
  setSkillUsers(
    skillId: string,
    userIds: string[],
    source?: AnalyticsSource,
  ): Promise<TUserSkillAssignment[]>;
  /** Replace a member's skills; `prior` is the set the caller showed before. */
  setMemberSkills(
    memberId: string,
    skillIds: string[],
    options?: { prior?: readonly string[]; source?: AnalyticsSource },
  ): Promise<TUserSkillAssignment[]>;
  /** Replace the signed-in member's own skills. */
  setMySkills(skillIds: string[]): Promise<void>;
}

/**
 * Re-read every cached skill read, the table's pages included, and resolve
 * once those on screen have answered; a failure only warns, the write landed.
 */
function settle(
  client: QueryClient,
  scope: QueryScope | null,
  op: string,
  id: string,
): Promise<void> {
  if (!scope) return Promise.resolve();
  return invalidateSkills(client, scope).catch((error: unknown) => {
    console.warn("[skills] invalidating the cached skills failed", {
      slug: scope.tenant,
      op,
      id,
      error,
    });
  });
}

/**
 * The skill writers (NG-2802). Each resolves with the server's answer once it
 * has the write, and rejects when it refused, so the caller keeps its own
 * toast and save state.
 *
 * A write puts its answer into the cached reads it changed at once (the bank,
 * the skill's row on the settings table's pages, a skill's holders, a member's
 * skills, the member's row in the members list, the signed-in member's own),
 * then marks every skill read stale so what is on screen re-reads, the table's
 * pages included. A create, import or delete resolves once those pages have
 * re-read (NG-2805), since only the server knows where a row sorts and whether
 * the filters still admit it, so a dialog that closes on success closes over
 * the saved list. Each emits its event once the write lands, never on a
 * failure (NG-2063).
 *
 * Reads the signed-in user from the user store, so the caller must be an
 * `observer`.
 */
export function useSkillWriters(workspaceSlug: string): SkillWriters {
  const { workspaceSkillService: service, workspaceMembers: members } = useStore();
  const client = useQueryClient();
  const liveScope = useQueryScope(workspaceSlug);
  const tenant = liveScope?.tenant;
  const principal = liveScope?.principal;
  const scope = useMemo<QueryScope | null>(
    () => (tenant !== undefined && principal !== undefined ? { tenant, principal } : null),
    [tenant, principal],
  );

  /** A skill as last read: the cached bank, else the table's cached pages. */
  const lastRead = useCallback(
    (skillId: string) =>
      scope
        ? (client.getQueryData<TSkill[]>(skillBankKey(scope))?.find((s) => s.id === skillId) ??
          cachedSkillsPageRow(client, scope, skillId))
        : undefined,
    [client, scope],
  );

  /** An edited skill into the bank and the table's pages, then the reads stale. */
  const written = useCallback(
    (skill: TSkill, op: string) => {
      if (scope) {
        patchSkillBank(client, scope, { written: skill });
        // In place: an edit keeps the row where the table shows it.
        patchSkillsPages(client, scope, skill.id, skill);
      }
      void settle(client, scope, op, skill.id);
    },
    [client, scope],
  );

  const createSkill = useCallback<SkillWriters["createSkill"]>(
    async (name, source = "page") => {
      const created = await service.create(workspaceSlug, { name });
      if (scope) patchSkillBank(client, scope, { written: created });
      // Before the re-read, so a failed one doesn't drop it (NG-2063).
      track("skill_created", { skill_id: created.id, source });
      await settle(client, scope, "create", created.id);
      return created;
    },
    [service, workspaceSlug, scope, client],
  );

  const bulkImport = useCallback<SkillWriters["bulkImport"]>(
    async (rows) => {
      const report = await service.bulkImport(workspaceSlug, rows);
      track("skills_bulk_imported", {
        created: report.created_count,
        reused: report.reused_count,
        errors: report.error_count,
        source: "import",
      });
      await settle(client, scope, "import", "");
      return report;
    },
    [service, workspaceSlug, client, scope],
  );

  const updateSkill = useCallback<SkillWriters["updateSkill"]>(
    async (skillId, data, source = "page") => {
      // Read before the write: `fields` is what this save changed.
      const fields = changedFields(data, lastRead(skillId));
      const updated = await service.update(workspaceSlug, skillId, data);
      written(updated, "update");
      track("skill_updated", { skill_id: skillId, fields, source });
      return updated;
    },
    [service, workspaceSlug, lastRead, written],
  );

  const archiveSkill = useCallback<SkillWriters["archiveSkill"]>(
    async (skillId, source = "page") => {
      const updated = await service.archive(workspaceSlug, skillId);
      written(updated, "archive");
      track("skill_archived", { skill_id: skillId, source });
      return updated;
    },
    [service, workspaceSlug, written],
  );

  const unarchiveSkill = useCallback<SkillWriters["unarchiveSkill"]>(
    async (skillId, source = "page") => {
      const updated = await service.unarchive(workspaceSlug, skillId);
      written(updated, "restore");
      track("skill_restored", { skill_id: skillId, source });
      return updated;
    },
    [service, workspaceSlug, written],
  );

  const deleteSkill = useCallback<SkillWriters["deleteSkill"]>(
    async (skillId, source = "page") => {
      await service.remove(workspaceSlug, skillId);
      track("skill_deleted", { skill_id: skillId, source });
      if (scope) {
        patchSkillBank(client, scope, { deletedId: skillId });
        // A deleted skill's holders would re-read a 404: drop them first.
        client.removeQueries({ queryKey: skillUsersKey(scope, skillId) });
      }
      await settle(client, scope, "delete", skillId);
    },
    [service, workspaceSlug, scope, client],
  );

  const setSkillUsers = useCallback<SkillWriters["setSkillUsers"]>(
    async (skillId, userIds, source = "page") => {
      // The call sends the whole list: count what it adds and removes against
      // the holders already read. A list never read counts only the adds.
      const prior = scope
        ? (client.getQueryData<TUserSkillAssignment[]>(skillUsersKey(scope, skillId)) ?? [])
        : [];
      const list = await service.setSkillUsers(workspaceSlug, skillId, userIds);
      const { added, removed } = listChanges(
        prior.map((a) => a.user),
        userIds,
      );
      if (added > 0) track("skill_assigned", { skill_id: skillId, count: added, source });
      if (removed > 0) track("skill_unassigned", { skill_id: skillId, count: removed, source });
      // The count and the preview on the skill's row, so the closed cell agrees
      // with the holders it just saved.
      const patch = { user_count: list.length, preview_user_ids: previewUserIds(list) };
      if (scope) {
        client.setQueryData(skillUsersKey(scope, skillId), list);
        patchSkillBank(client, scope, { patched: skillId, patch });
        patchSkillsPages(client, scope, skillId, patch);
      }
      void settle(client, scope, "set-users", skillId);
      // The members list carries each member's skills: re-read it.
      members.invalidateCachedMembers(workspaceSlug).catch((error: unknown) => {
        console.warn("[skills] invalidating the cached members failed", {
          slug: workspaceSlug,
          skillId,
          error,
        });
      });
      return list;
    },
    [scope, client, service, workspaceSlug, members],
  );

  const setMemberSkills = useCallback<SkillWriters["setMemberSkills"]>(
    async (memberId, skillIds, { prior = [], source = "page" } = {}) => {
      const list = await service.setMemberSkills(workspaceSlug, memberId, skillIds);
      // A person's skills, set from their side: the same events, keyed by member.
      const { added, removed } = listChanges(prior, skillIds);
      if (added > 0) track("skill_assigned", { member_id: memberId, count: added, source });
      if (removed > 0) track("skill_unassigned", { member_id: memberId, count: removed, source });
      if (scope) client.setQueryData(memberSkillsKey(scope, memberId), list);
      // The member's row in the members list shows the saved skills at once.
      const saved = list.map((a) => a.skill);
      members.patchMember(memberId, { skill_ids: saved, skill_count: saved.length });
      // The holders and the counts of every skill the change touched.
      void settle(client, scope, "set-member", memberId);
      return list;
    },
    [service, workspaceSlug, scope, client, members],
  );

  const setMySkills = useCallback<SkillWriters["setMySkills"]>(
    async (skillIds) => {
      await service.setMySkills(workspaceSlug, skillIds);
      if (scope) client.setQueryData(mySkillsKey(scope), skillIds);
      void settle(client, scope, "set-mine", "");
      members.invalidateCachedMembers(workspaceSlug).catch((error: unknown) => {
        console.warn("[skills] invalidating the cached members failed", {
          slug: workspaceSlug,
          error,
        });
      });
    },
    [service, workspaceSlug, scope, client, members],
  );

  return useMemo(
    () => ({
      createSkill,
      bulkImport,
      updateSkill,
      archiveSkill,
      unarchiveSkill,
      deleteSkill,
      setSkillUsers,
      setMemberSkills,
      setMySkills,
    }),
    [
      createSkill,
      bulkImport,
      updateSkill,
      archiveSkill,
      unarchiveSkill,
      deleteSkill,
      setSkillUsers,
      setMemberSkills,
      setMySkills,
    ],
  );
}
